/**
 * Service-worker update prompt.
 *
 * Shown when a new service worker has installed and is waiting for activation.
 * Renders a centred modal-style card with a clear Reload button so the user
 * explicitly opts in to refreshing — important for a medical logging tool
 * where mid-edit auto-reloads would lose work.
 */

let visible = false;

export function showUpdatePrompt(onReload: () => void): void {
  if (visible) return;
  visible = true;

  const overlay = document.createElement("div");
  overlay.setAttribute("role", "alertdialog");
  overlay.setAttribute("aria-labelledby", "update-prompt-title");
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(31, 45, 61, 0.45);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  `;

  const card = document.createElement("div");
  card.style.cssText = `
    background: #fff;
    color: #1f2d3d;
    width: 100%;
    max-width: 22rem;
    padding: 1.5rem 1.5rem 1.25rem;
    border-radius: 1rem;
    box-shadow: 0 12px 40px rgba(31, 45, 61, 0.25);
    text-align: center;
    font-family: inherit;
  `;

  const icon = document.createElement("div");
  icon.textContent = "⟳";
  icon.style.cssText = `
    font-size: 2rem;
    line-height: 1;
    color: #bd64fc;
    margin-bottom: 0.5rem;
  `;
  card.appendChild(icon);

  const title = document.createElement("h2");
  title.id = "update-prompt-title";
  title.textContent = "Update available";
  title.style.cssText = `
    margin: 0 0 0.5rem;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1f2d3d;
  `;
  card.appendChild(title);

  const body = document.createElement("p");
  body.textContent =
    "A new version of DAFNE Log is ready. Reload now to get the latest changes. Make sure you've saved any in-progress entry first.";
  body.style.cssText = `
    margin: 0 0 1.25rem;
    font-size: 0.9rem;
    line-height: 1.4;
    color: #495057;
  `;
  card.appendChild(body);

  const actions = document.createElement("div");
  actions.style.cssText = `
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  `;

  const dismissBtn = document.createElement("button");
  dismissBtn.type = "button";
  dismissBtn.textContent = "Not now";
  dismissBtn.style.cssText = `
    background: #f1f3f5;
    color: #495057;
    border: none;
    padding: 0.6rem 1.1rem;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    flex: 1;
    max-width: 9rem;
  `;
  dismissBtn.addEventListener("click", () => {
    overlay.remove();
    visible = false;
  });
  actions.appendChild(dismissBtn);

  const reloadBtn = document.createElement("button");
  reloadBtn.type = "button";
  reloadBtn.textContent = "Reload";
  reloadBtn.style.cssText = `
    background: #bd64fc;
    color: #fff;
    border: none;
    padding: 0.6rem 1.1rem;
    border-radius: 999px;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    flex: 1;
    max-width: 9rem;
    box-shadow: 0 2px 8px rgba(189, 100, 252, 0.35);
  `;
  reloadBtn.addEventListener("click", () => {
    reloadBtn.disabled = true;
    dismissBtn.disabled = true;
    reloadBtn.textContent = "Updating…";
    onReload();
  });
  actions.appendChild(reloadBtn);

  card.appendChild(actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}
